import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  GerarIdRequest,
  GetImportarPlanoAulaOutraEscolaRequest,
  IdPlanoDeAula,
  PlanoDeAulaDisciplina,
  SavePlanoAulaRequest,
  VoltarIdRequest,
} from '../models';
import { PLANO_AULA_API_URL } from './plano-aula-api.config';

/**
 * Serviço responsável por salvar dados de plano de aula
 * Centraliza todas as chamadas HTTP de escrita
 */
@Injectable({
  providedIn: 'root',
})
export class PlanoAulaSaveService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(PLANO_AULA_API_URL);

  /**
   * Salva plano de aula
   */
  savePlanoAula(request: SavePlanoAulaRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/planoaula`, request.json, { params: {
        tipo: 'PLANO_AULA',
        DATABASE: request.DATABASE,
      },
    });
  }

  /**
   * Salva disciplina vinculada a um plano de aula
   */
  saveDisciplina(
    json: string,
    database: string,
  ): Observable<PlanoDeAulaDisciplina> {
    return this.http.post<PlanoDeAulaDisciplina>(
      `${this.apiUrl}/planoaula`,
      json,
      {
        headers: {
          JSON: json,
        },
        params: {
          tipo: 'PLANO_AULA_DISCIPLINAS',
          DATABASE: database,
        },
      },
    );
  }

  /**
   * Gera novo ID para plano de aula
   */
  gerarID(request: GerarIdRequest): Observable<IdPlanoDeAula> {
    const params = new HttpParams()
      .append('tipo', 'GERAR_ID')
      .append('CPF', request.CPF)
      .append('DATABASE', request.DATABASE);

    return this.http.get<IdPlanoDeAula>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Volta ID de plano de aula (desfaz geração)
   */
  voltarID(request: VoltarIdRequest): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'VOLTAR_ID')
      .append('CPF', request.CPF)
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, null, { params,
    });
  }

  /**
   * Restaura plano de aula excluído
   */
  postRestaurarPlanoExcluido(
    request: { DATABASE: string },
    body: object[],
  ): Observable<any> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_RECUPERA_EXCLUIDO')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, body, { params,
    });
  }

  /**
   * Clona plano de aula
   */
  clonarPlanoDeAula(request: {
    ID_PLANO_AULA: number;
    CPF: string;
    DATABASE: string;
    json: string;
  }): Observable<{ AVISO: string }[]> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_CLONAR')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('CPF', request.CPF)
      .append('DATABASE', request.DATABASE);

    return this.http.get<{ AVISO: string }[]>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Exclui disciplina vinculada ao plano de aula
   */
  deletePLANO_AULA_DISCIPLINAS(request: {
    payload: PlanoDeAulaDisciplina;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_DISCIPLINAS')
      .append('ID_PLANO_AULA', request.payload.ID_PLANO_AULA)
      .append('CDDISC', request.payload.CDDISC)
      .append('DATABASE', request.DATABASE);

    return this.http.delete(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Exclui plano de aula
   */
  deletePlanoAula(request: {
    ID_PLANO_AULA: number;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('DATABASE', request.DATABASE);

    return this.http.delete(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Exclui recurso vinculado ao plano de aula
   */
  deleteLKP_PLANO_AULA_RECURSO(request: {
    ID_PLANO_AULA: number;
    ID_RECURSO: number;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_RECURSOS')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('ID_RECURSO', request.ID_RECURSO)
      .append('DATABASE', request.DATABASE);

    return this.http.delete(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Exclui atividade vinculada ao plano de aula
   */
  deleteLKP_ATIVIDADES(request: {
    ID_PLANO_AULA: number;
    ID_ATIVIDADE: number;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ATIVIDADES')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('ID_ATIVIDADE', request.ID_ATIVIDADE)
      .append('DATABASE', request.DATABASE);

    return this.http.delete(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Adiciona atividade ao plano de aula
   */
  postLKP_ATIVIDADES(request: {
    DATABASE: string;
    JSON: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ATIVIDADES')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, request.JSON, {
      headers: {
        JSON: request.JSON,
      },
      params,
    });
  }

  /**
   * Adiciona recurso ao plano de aula
   */
  postLKP_PLANO_AULA_RECURSO(request: {
    DATABASE: string;
    JSON: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_RECURSOS')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, request.JSON, {
      headers: {
        JSON: request.JSON,
      },
      params,
    });
  }

  /**
   * Salva etapa parte diversificada (BNCC ETAPA=4)
   */
  postEtapaDiv(database: string, json: string): Observable<any> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_ETAPA_DIV')
      .append('DATABASE', database);

    return this.http.post(`${this.apiUrl}/planoaula`, json, {
      headers: {
        JSON: json,
      },
      params,
    });
  }

  /**
   * Exclui habilidade BNCC vinculada ao plano (EtapaV2)
   */
  deletePLANO_AULA_ETAPA_V2(request: {
    ID_PLANO_AULA_HAB: number;
    ID_BNCC: number;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_ETAPA_V2')
      .append('ID_PLANO_AULA_HAB', request.ID_PLANO_AULA_HAB)
      .append('ID_BNCC', request.ID_BNCC)
      .append('DATABASE', request.DATABASE);

    return this.http.delete(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Adiciona habilidades BNCC Educação Infantil (ETAPA=1)
   */
  postPLANO_AULA_ETAPA_EI(request: {
    json: string;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EI')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, null, {
      headers: {
        JSON: request.json,
      },
      params,
    });
  }

  /**
   * Adiciona habilidades BNCC Ensino Fundamental (ETAPA=2)
   */
  postPLANO_AULA_ETAPA_EF(request: {
    json: string;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EF')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, request.json, {
      headers: {
        JSON: request.json,
      },
      params,
    });
  }

  /**
   * Adiciona habilidades BNCC Ensino Médio (ETAPA=3)
   */
  postPLANO_AULA_ETAPA_EM(request: {
    json: string;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_ETAPA_EM')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, request.json, {
            params,
    });
  }

  /**
   * Adiciona habilidades BNCC EJA (ETAPA=10)
   */
  postPLANO_AULA_ETAPA_EJA(request: {
    json: string;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EJA')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, request.json, {
      headers: {
        JSON: request.json,
      },
      params,
    });
  }

  /**
   * Adiciona habilidade BNCC Parte Diversificada (ETAPA=4) - modal BNCC
   */
  postPLANO_AULA_ETAPA_DIV(request: {
    json: string;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_DIV')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, null, {
      headers: {
        JSON: request.json,
      },
      params,
    });
  }

  /**
   * Exclui habilidade BNCC Educação Infantil (ETAPA=1)
   */
  deletePLANO_AULA_ETAPA_EI(request: {
    ID_PLANO_AULA: number;
    ID_BNCC_EI: number;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EI')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('ID_BNCC_EI', request.ID_BNCC_EI)
      .append('DATABASE', request.DATABASE);

    return this.http.delete(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Exclui habilidade BNCC Ensino Fundamental (ETAPA=2)
   */
  deletePLANO_AULA_ETAPA_EF(request: {
    ID_PLANO_AULA: number;
    ID_BNCC_EF: number;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EF')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('ID_BNCC_EF', request.ID_BNCC_EF)
      .append('DATABASE', request.DATABASE);

    return this.http.delete(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Exclui habilidade BNCC Ensino Médio (ETAPA=3)
   */
  deletePLANO_AULA_ETAPA_EM(request: {
    ID_PLANO_AULA: number;
    ID_BNCC_EM: number;
    DATABASE: string;
  }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EM')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('ID_BNCC_EM', request.ID_BNCC_EM)
      .append('DATABASE', request.DATABASE);

    return this.http.delete(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Altera situação do plano de aula
   */
  postSituacao(request: { DATABASE: string; JSON: string }): Observable<any> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_SITUACAO')
      .append('DATABASE', request.DATABASE);

    return this.http.post(`${this.apiUrl}/planoaula`, request.JSON, {
      headers: {
        JSON: request.JSON,
      },
      params,
    });
  }

  /**
   * Importa um plano de aula de outra escola para a turma destino.
   */
  importarPlanoOutraEscola(
    request: GetImportarPlanoAulaOutraEscolaRequest,
  ): Observable<{ AVISO?: string }[]> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_IMPORTAR_OUTRA_ESCOLA')
      .append('ID_PLANO_AULA_ORIGEM', request.ID_PLANO_AULA_ORIGEM)
      .append('CDESCOLAORIGEM', request.CDESCOLAORIGEM)
      .append('CDESCOLADESTINO', request.CDESCOLADESTINO)
      .append('CPF', request.CPF)
      .append('ID_TURMA_DESTINO', request.ID_TURMA_DESTINO)
      .append('DATABASE', request.DATABASE);

    return this.http.get<{ AVISO?: string }[]>(`${this.apiUrl}/planoaula`, {
      params,
    });
  }
}
